<template>
  <v-main class="pt-0">
    <v-container class="maxContainerWidth mb-16" style="height: 100%">
      <!-- Make a Payment Header -->
      <v-row class="text-white mt-4 mb-4">
        <v-col class="mr-4 d-flex align-center">
          <img
            data-v-55fdbe26=""
            src="../../assets/ChangeBillingMethod.02503efe.svg"
            alt="Change Billing"
            width="48"
          />
          <h1 class="text-white pl-2">
            {{ $t('L_change_billing_method') }}
          </h1>
        </v-col>
      </v-row>
      <MessageDialog
        :show-dialog="dialog"
        :has-button-two="true"
        type="warning"
        :title="$t('L_cancel_changes')"
        :message="$t('L_by_cancelling_no_change')"
        :button-two-label="$t('L_yes_cancel')"
        :button-one-label="$t('L_no_continue')"
        :is-persistent="true"
        @buttonTwoAction="closeAndGoToPolicyDetails"
        @buttonOneAction="dialog = false"
      />
      <NavigationConfirmationDialog
        v-if="showNavigationConfirmationDialog"
        @confirm-navigation="navigateToRoute"
        @cancel-navigation="showNavigationConfirmationDialog = false"
      />
      <v-stepper v-model="step" :vertical="$vuetify?.display?.xs" class="mb-16">
        <!-- Stepper Header for non-xs screens -->
        <v-stepper-header v-show="!$vuetify?.display?.xs">
          <v-stepper-item :complete="step > 1" value="1" class="stepperTextSize">
            {{ $t('L_billing') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="step > 2" value="2" class="stepperTextSize">
            <span v-if="selectedType === '1bd'">
              {{ $t('L_bank_info') }}
            </span>
            <span v-else>
              {{ $t('L_review') }}
            </span>
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="step > 3" value="3" class="stepperTextSize">
            <span v-if="selectedType === '1bd'">
              {{ $t('L_withdrawal_day') }}
            </span>
            <span v-else>
              {{ $t('L_confirmation') }}
            </span>
          </v-stepper-item>
          <v-stepper-item
            :complete="step > 4"
            value="4"
            class="stepperTextSize"
            v-if="selectedType === '1bd'"
          >
            {{ $t('L_review') }}
          </v-stepper-item>
          <v-stepper-item
            :complete="step > 5"
            value="5"
            class="stepperTextSize"
            v-if="selectedType === '1bd'"
          >
            {{ $t('L_confirmation') }}
          </v-stepper-item>
        </v-stepper-header>

        <!-- Stepper Content -->
        <v-stepper-window>
          <!-- Step 1 -->
          <v-stepper-item
            :complete="step > 1"
            value="1"
            v-show="$vuetify?.display?.xs"
            class="pl-0 pr-0 pt-0"
            :class="step === '1' ? 'my-step-complete' : ''"
          >
            {{ $t('L_billing') }}
          </v-stepper-item>
          <v-stepper-window-item value="1" class="pl-0 pr-0 pt-0">
            <PayMethodChangeStepOne
              :policy-data="currentPolicy"
              :selected-option="selectedType"
              @paymentAmount="setPaymentAmount"
              @goToStepTwo="goToStepTwo"
              @cancel="dialog = true"
            />
          </v-stepper-window-item>

          <!-- Step 2 -->
          <v-stepper-item
            v-if="$vuetify?.display?.xs"
            :complete="step > 2"
            value="2"
            class="pl-0 pr-0 pt-0"
            :class="step === '2' ? 'my-step-complete' : ''"
          >
            <span v-if="selectedType === '1bd'">
              {{ $t('L_bank_info') }}
            </span>
            <span v-else>
              {{ $t('L_review') }}
            </span>
          </v-stepper-item>
          <v-stepper-window-item value="2" v-if="selectedType !== '1bd'" class="pl-0 pr-0 pt-0">
            <PayMethodChangeStepFour
              :selected-option="selectedType"
              :bank-info="bankInfo"
              :payment-amount="paymentAmount"
              :w-day="wDay"
              :next-payment-due-date="nextPaymentDueDate"
              :prop-is-paid-to-date-in-future="isPaidToDateInFuture"
              :prop-is-past-due="isPastDue"
              :prop-is-user-sel-date-earlier-than-two-days-from-now="
                isUserSelDateEarlierThanTwoDaysFromNow
              "
              @pageOne="goToStepOne"
              @pageTwo="goToStepTwo"
              @pageThree="goToStepThree"
              @pageFive="goToStepFive"
              @cancel="dialog = true"
              @showFirstMessage="setShowFirstMessage"
              @showSecondMessage="setShowSecondMessage"
              @isShowMessage="setIsShowMessage"
              @immediatePayAmount="setImmediatePayAmount"
              @paymentDueDate="setPaymentDueDate"
              @isPastDueAndOnDirectBill="setIsPastDueAndOnDirectBill"
            />
          </v-stepper-window-item>
          <v-stepper-window-item value="2" v-else class="pl-0 pr-0 pt-0">
            <PayMethodChangeStepTwo
              :is-update-bank="isUpdateBank"
              @goToStepOne="goToStepOne"
              @goToStepThree="goToStepThree"
              @cancel="dialog = true"
            />
          </v-stepper-window-item>

          <!-- Step 3 -->
          <v-stepper-item
            v-show="$vuetify?.display?.xs"
            :complete="step > 3"
            value="3"
            class="pl-0 pr-0 pt-0"
            :class="step === '3' ? 'my-step-complete' : ''"
          >
            <span v-if="selectedType === '1bd'">
              {{ $t('L_withdrawal_day') }}
            </span>
            <span v-else>
              {{ $t('L_confirmation') }}
            </span>
          </v-stepper-item>
          <v-stepper-window-item value="3" class="pl-0 pr-0 pt-0">
            <div v-if="selectedType !== '1bd'">
              <PayMethodChangeStepFive
                :selected-option="selectedType"
                :bank-info="bankInfo"
                :prop-immediate-pay-amount="immediatePayAmount"
                :prop-payment-amount="paymentAmount"
                :prop-payment-due-date="paymentDueDate"
                :prop-is-past-due-and-on-direct-bill="isPastDueAndOnDirectBill"
                :prop-is-paid-to-date-in-future="isPaidToDateInFuture"
                :prop-is-show-message="isShowMessage"
                :prop-is-past-due="isPastDue"
                :prop-show-first-message="showFirstMessage"
                :prop-show-second-message="showSecondMessage"
                @done="goToStepSix"
              />
            </div>
            <div v-if="selectedType === '1bd'">
              <PayMethodChangeStepThree
                :w-day="wDay || '-1'"
                @goToStepFourBankDraft="goToStepFourBankDraft"
                @isPaidToDateInFuture="setIsPaidToDateInFuture"
                @isPastDue="setIsPastDue"
                @isUserSelDateEarlierThanTwoDaysFromNow="setIsUserSelDateEarlierThanTwoDaysFromNow"
                @cancel="dialog = true"
              />
            </div>
          </v-stepper-window-item>

          <!-- Step 4 (only for 1bd) -->
          <v-stepper-item
            :complete="step > 4"
            value="4"
            v-if="$vuetify?.display?.xs && selectedType === '1bd'"
            class="pl-0 pr-0 pt-0"
            :class="step === '4' ? 'my-step-complete' : ''"
          >
            {{ $t('L_review') }}
          </v-stepper-item>
          <v-stepper-window-item value="4" v-if="selectedType === '1bd'" class="pl-0 pr-0 pt-0">
            <PayMethodChangeStepFour
              :selected-option="selectedType"
              :bank-info="bankInfo"
              :payment-amount="paymentAmount"
              :w-day="wDay"
              :next-payment-due-date="nextPaymentDueDate"
              :prop-is-paid-to-date-in-future="isPaidToDateInFuture"
              :prop-is-past-due="isPastDue"
              :prop-is-user-sel-date-earlier-than-two-days-from-now="
                isUserSelDateEarlierThanTwoDaysFromNow
              "
              @pageOne="goToStepOne"
              @pageTwo="goToStepTwo"
              @pageThree="goToStepThree"
              @pageFive="goToStepFive"
              @cancel="dialog = true"
              @showFirstMessage="setShowFirstMessage"
              @showSecondMessage="setShowSecondMessage"
              @isShowMessage="setIsShowMessage"
              @immediatePayAmount="setImmediatePayAmount"
              @paymentDueDate="setPaymentDueDate"
              @isPastDueAndOnDirectBill="setIsPastDueAndOnDirectBill"
            />
          </v-stepper-window-item>

          <!-- Step 5 (only for 1bd) -->
          <v-stepper-item
            :complete="step > 5"
            value="5"
            v-if="$vuetify?.display?.xs && selectedType === '1bd'"
            class="pl-0 pr-0 pt-0"
            :class="step === '5' ? 'my-step-complete' : ''"
          >
            {{ $t('L_confirmation') }}
          </v-stepper-item>
          <v-stepper-window-item value="5" v-if="selectedType === '1bd'" class="pl-0 pr-0 pt-0">
            <PayMethodChangeStepFive
              :selected-option="selectedType"
              :bank-info="bankInfo"
              :prop-immediate-pay-amount="immediatePayAmount"
              :prop-payment-amount="paymentAmount"
              :prop-payment-due-date="paymentDueDate"
              :prop-is-past-due-and-on-direct-bill="isPastDueAndOnDirectBill"
              :prop-is-paid-to-date-in-future="isPaidToDateInFuture"
              :prop-is-show-message="isShowMessage"
              :prop-is-past-due="isPastDue"
              :prop-show-first-message="showFirstMessage"
              :prop-show-second-message="showSecondMessage"
              @done="goToStepSix"
            />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
    </v-container>
    <FooterComponent />
    <NavigationConfirmationDialog
      v-if="showNavigationConfirmationDialog"
      @confirm-navigation="navigateToRoute"
      @cancel-navigation="showNavigationConfirmationDialog = false"
    />
  </v-main>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapState, mapActions } from 'pinia';
import MessageDialog from '@/components/MessageDialog.vue';
import PayMethodChangeStepOne from './PayMethodChangeStepOne.vue';
import PayMethodChangeStepTwo from './PayMethodChangeStepTwo.vue';
import PayMethodChangeStepThree from './PayMethodChangeStepThree.vue';
import PayMethodChangeStepFour from './PayMethodChangeStepFour.vue';
import PayMethodChangeStepFive from './PayMethodChangeStepFive.vue';
// import VideoPlayer from '../../../../../components/VideoPlayer/VideoPlayer.vue';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import type { IPolicyStoreBankInfo } from '@/modules/policy/store/policyStore.model';
import NavigationConfirmationDialog from '@/components/NavigationConfirmation/NavigationConfirmationDialog.vue';
import { mdiCurrencyUsd } from '@mdi/js';
import FooterComponent from '@/components/Footer/Footer.vue';
import type { RouteParamsGeneric } from 'vue-router';

const componentOptions: ComponentOptions = {
  name: 'PaymentMethodLayout',
  components: {
    PayMethodChangeStepOne,
    PayMethodChangeStepTwo,
    PayMethodChangeStepThree,
    PayMethodChangeStepFour,
    PayMethodChangeStepFive,
    MessageDialog,
    NavigationConfirmationDialog,
    FooterComponent,
    // VideoPlayer,
  },
  data: () => ({
    selectedType: '-1',
    showDatePicker: false,
    isUpdateBank: true,
    persistent: true,
    dialog: false,
    step: '1',
    bankInfo: {} as IPolicyStoreBankInfo,
    wDay: '-1',
    immediatePayAmount: '',
    paymentAmount: '', // Needed for last page since after backend call data gets reset in Bank Draft Step 4.
    paymentDueDate: '', // Needed for last page since after backend call data gets reset in Bank Draft Step 4.
    isPastDueAndOnDirectBill: false, // Needed for last page since after backend call data gets reset in Bank Draft Step 4.
    selectedMethod: '', // Checking || Savings
    src: '',
    poster: '',
    closedcaption: '',
    nextPaymentDueDate: '',
    isPaidToDateInFuture: false,
    isPastDue: false,
    isUserSelDateEarlierThanTwoDaysFromNow: false,
    showFirstMessage: false,
    showSecondMessage: false,
    isShowMessage: false,
    showNavigationConfirmationDialog: false,
    navigatingToRoute: '',
    navigatingToParams: {} as RouteParamsGeneric,
    navigationAwayConfirmed: false,
    currentPolicyNumber: '',
    mdiCurrencyUsd,
    locales: [
      { title: 'English (US)', value: 'en-US' },
      { title: 'Español (US)', value: 'es-US' },
      { title: 'English (CAN)', value: 'en-CA' },
      { title: 'French (CAN)', value: 'fr-CA' },
    ],
  }),
  computed: {
    ...mapState(usePolicyStore, [
      'currentPolicy',
      'selectedPolicyIndex',
      'policies',
      'payMethodChange',
    ]),
    getValueForReviewStep() {
      if (this.selectedType === '1bd') {
        return '5';
      }
      return '3';
    },
    getValueForConfirmationStep() {
      if (this.selectedType === '1bd') {
        return '6';
      }
      return '4';
    },
    reviewStep() {
      if (this.selectedType === '1bd') {
        return '5';
      }
      return '3';
    },
    confirmationStep() {
      if (this.selectedType === '1bd') {
        return '6';
      }
      return '4';
    },
    dynamicReviewStep() {
      return '5';
    },
    dynamicConfirmationStep() {
      return '6';
    },
    getPolicyNum() {
      return this.currentPolicy?.contractId || '';
    },
  },
  methods: {
    ...mapActions(usePolicyStore, ['setPayMethodChange', 'resetBankInfo', 'setPayMethodChange']),
    goToStepOne() {
      this.step = '1';
    },
    goToStepTwo(selectedType: string | undefined) {
      let st;
      this.nextPaymentDueDate = this.currentPolicy?.data?.nextPaymentDueDate;
      if (selectedType === '' || selectedType === undefined) {
        // User coming from Review Page back here to change Payment Option
        st = this.selectedType;
        this.step = '2';
      } else {
        st = selectedType;
        if (selectedType !== '1bd') {
          this.step = '2';
        } else {
          this.step = '2';
        }
      }
      this.selectedType = st;
    },
    goToStepThree(bankInfo: IPolicyStoreBankInfo | undefined) {
      let st;
      if (bankInfo === undefined) {
        st = this.bankInfo;
      } else {
        st = bankInfo;
      }
      this.bankInfo = st;
      this.step = '3';
    },
    goToStepFourBankDraft(withdrawalDay: string) {
      this.wDay = withdrawalDay;
      this.bankInfo.draftDay = withdrawalDay;
      this.step = '4';
    },
    goToStepFive() {
      if (this.selectedType === '1bd') {
        this.step = '5';
      } else {
        this.step = '3';
      }
    },
    goToStepSix() {
      this.step = -1;
      // Reset all data
      this.selectedType = '-1';
      this.bankInfo = {};
      this.wDay = '-1';
      this.setPayMethodChange(-1);
      this.$router.push({
        name: 'PolicyDetails',
        params: { contractId: this.selectedPolicyIndex },
      });
    },
    setImmediatePayAmount(immediatePayAmount: string) {
      this.immediatePayAmount = immediatePayAmount;
    },
    setPaymentAmount(paymentAmount: string) {
      this.paymentAmount = paymentAmount;
    },
    setPaymentDueDate(paymentDueDate: string) {
      this.paymentDueDate = paymentDueDate;
    },
    setIsPastDueAndOnDirectBill(isPastDueAndOnDirectBill: boolean) {
      this.isPastDueAndOnDirectBill = isPastDueAndOnDirectBill;
    },
    setIsPaidToDateInFuture(isPaidToDateInFuture: boolean) {
      this.isPaidToDateInFuture = isPaidToDateInFuture;
    },
    setIsPastDue(isPastDue: boolean) {
      this.isPastDue = isPastDue;
    },
    setIsShowMessage(isShowMessage: boolean) {
      this.isShowMessage = isShowMessage;
    },
    setIsUserSelDateEarlierThanTwoDaysFromNow(isUserSelDateEarlierThanTwoDaysFromNow: boolean) {
      this.isUserSelDateEarlierThanTwoDaysFromNow = isUserSelDateEarlierThanTwoDaysFromNow;
    },
    setShowFirstMessage(showFirstMessage: boolean) {
      this.showFirstMessage = showFirstMessage;
    },
    setShowSecondMessage(showSecondMessage: boolean) {
      this.showSecondMessage = showSecondMessage;
    },
    closeAndGoToPolicyDetails() {
      this.navigationAwayConfirmed = true;
      this.setPayMethodChange(-1);
      this.$router.push({
        name: 'PolicyDetails',
        params: { contractId: this.selectedPolicyIndex },
      });
    },
    async navigateToRoute() {
      this.navigationAwayConfirmed = true;
      await this.$router.push({
        name: this.navigatingToRoute,
        params: this.navigatingToParams,
      });
    },
  },
  async mounted() {
    // Clearing out the Bank Name from previous session
    // await this.$store.dispatch(usePolicyStore, 'resetBankInfo', '');
    await this.resetBankInfo('');
    this.currentPolicyNumber = this.currentPolicy?.contractId || '';
    this.selectedType = '-1';
    this.showDatePicker = false;
    this.isUpdateBank = true;
    this.persistent = true;
    this.dialog = false;
    this.step = '1';
    this.bankInfo = {};
    this.wDay = '-1';
    this.immediatePayAmount = '';
    this.paymentAmount = ''; // Needed for last page since after backend call data gets reset in Bank Draft Step 4.
    this.paymentDueDate = ''; // Needed for last page since after backend call data gets reset in Bank Draft Step 4.
    this.selectedMethod = ''; // Checking || Savings
    this.src = '';
    this.poster = '';
    this.closedcaption = '';
  },
  watch: {
    $route() {
      alert('Route changed');
    },
  },
  beforeRouteLeave(to, _, next) {
    const isProcessDone = this.payMethodChange === -1 || this.navigationAwayConfirmed;
    // const navigatingOutsideMinorAccountLink = to.path !== '/policy';
    // navigatingOutsideMinorAccountLink &&
    if (!isProcessDone) {
      this.showNavigationConfirmationDialog = true; // show NavigationConfirmationDialog
      this.navigatingToRoute = to.name as string; // save the route name
      this.navigatingToParams = to.params;
      next(false); // cancel the navigation
    } else {
      next();
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
@media only screen and (min-width: 600px) {
  .v-stepper-window {
    margin: 0rem !important;
  }
}
section {
  padding: 0;
}

.maxContainerWidth {
  max-width: 1024px;
  margin: auto;
}

/*--------------------------------------------------------------
# Videos
--------------------------------------------------------------*/
#videos #main {
  margin-top: 15vh;
}

video {
  background: #000;
}

.video-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.video-container .bi.bi-play-btn,
.video-container .bi.bi-play-btn-fill {
  position: absolute;
  height: auto;
  top: calc(50% - 2.5vw);
  padding: 0 0.5em;
  width: 25%;
  display: flex;
  justify-content: space-around;
  opacity: 1;
  transition: opacity 0.4s;
  z-index: 9997;
  color: #cc3333;
  font-size: 5vw !important;
}

.video-container .controls {
  position: absolute;
  background: #044c7f;
  height: 2em;
  bottom: 0em;
  padding: 0 0.5em;
  width: 100%;
  display: flex;
  justify-content: space-around;
  opacity: 0.05;
  transition: opacity 0.4s;
}

.video-container:hover .controls {
  opacity: 1;
}

.video-container .controls button {
  background: transparent;
  color: #fff;
  text-shadow: 0px 1px 5px rgba(0, 0, 0, 0.4);
  border: none;
  cursor: pointer;
  padding-bottom: 2px;
}

.video-container .controls .timeline {
  flex: 1;
  display: flex;
  align-items: center;
  border: none;
}

.video-container .controls .timeline .bar {
  background: rgba(1, 1, 65, 0.5);
  height: 4px;
  flex: 1;
}

.video-container .controls .timeline .bar .inner {
  background: #ccc;
  width: 0%;
  height: 100%;
}

.video-container .bi {
  font-size: 1.4em !important;
}

.video-container .bi::before,
.video-container [class^='bi-']::before,
.video-container [class*=' bi-']::before {
  vertical-align: -0.2em !important;
}

@media (max-width: 575px) {
  video-container .bi.bi-play-btn,
  .video-container .bi.bi-play-btn-fill {
    top: calc(50% - 5vw);
    font-size: 10vw !important;
  }
}
</style>
